import { useClerk, useUser } from '@clerk/react-router';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsClient } from 'usehooks-ts';

import { AUTHENTICATE_AS } from '@/lib/constants';
import { useAppState } from '@/lib/hooks/use-app-state';
import useRouteType from '@/lib/hooks/use-route-type';

import { LoadingBlockFullScreen } from '../loading/loading-block-full-screen';
import { Button } from '../ui/button';

import { getFullName } from '@/utils/name';
import { RedirectToSignIn } from '@clerk/react-router';
import { useAppStateMachine } from './app-state-machine-provider';

export const isClerkDisabled = !!AUTHENTICATE_AS;

interface Props {
    children: React.ReactNode | React.ReactNode[];
}

export const AppStateRedirectProvider: React.FC<Props> = ({ children }) => {
    const { isSuperAdminRoute, isPublicRoute } = useRouteType();
    const { state, send } = useAppStateMachine();
    const { platoUser } = useAppState();
    const [lastState, setLastState] = useState(state.value);
    const navigate = useNavigate();
    const { user, isLoaded } = useUser();
    const { signOut } = useClerk();
    const isClient = useIsClient();

    const renderLoading = () => {
        return <LoadingBlockFullScreen />;
    };

    useEffect(() => {
        if ((user || isClerkDisabled) && state.matches('NOT_AUTHENTICATED')) {
            send('USER_LOGGED_IN');
        }
    }, [user, isClerkDisabled]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('App state changed to: ', state.value);
        }
        lastState !== state.value && setLastState(state.value);
    }, [state]);

    const exitImpersonation = () => {
        // Navigate back to the customer's view
        const customerUrl = `/admin/customers/${platoUser?.platoCustomer.id}`;
        send('END_IMPERSONATING_SESSION');
        navigate(customerUrl, { replace: true });
    };

    const renderChildren = () => {
        return (
            <>
                {children}
                {state.matches('IMPOERSONATION_SESSION') && (
                    <div className="pointer-events-none fixed left-0 top-0 z-50 flex w-screen items-center justify-center opacity-10 hover:opacity-100">
                        <div className="pointer-events-auto flex flex-row items-center bg-neutral-100 px-2 hover:shadow-md">
                            <div className="mr-3 flex shrink-0 flex-col text-sm font-semibold">
                                <span className="font-normal text-neutral-400">
                                    <span className="text-neutral-600">
                                        {getFullName(platoUser?.firstName, platoUser?.lastName)}{' '}
                                        {platoUser?.platoCustomer.name &&
                                            `(${platoUser?.platoCustomer.name})`}
                                    </span>
                                </span>
                            </div>
                            <div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex shrink-0"
                                    onClick={exitImpersonation}
                                >
                                    Exit Demo
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    if (isPublicRoute) {
        // If user is authenticated on a public route (like /sign-in), redirect them
        if (state.matches('SUPER_ADMIN')) {
            navigate('/admin');
            return renderLoading();
        } else if (state.matches('PLATO_USER') || state.matches('IMPOERSONATION_SESSION')) {
            navigate('/');
            return renderLoading();
        }
        return renderChildren();
    } else if (!isClient) return renderLoading();
    else if (state.matches('LOGGING_OUT_OF_CLERK')) {
        signOut().then(() => {
            send('DID_LOGOUT_OF_CLERK');
            navigate('/sign-in');
        });
        return renderLoading();
    } else if (state.matches('NOT_AUTHENTICATED')) {
        // Only redirect if Clerk is loaded AND there's no user
        // If user exists, wait for state machine to transition
        if (isLoaded && !user && !isClerkDisabled) {
            return <RedirectToSignIn />;
        }
        return renderLoading();
    } else if (
        [
            'NOT_AUTHENTICATED',
            'LOADED_CLERK',
            'LOADING_PLATO_USER',
            'LOADING_SUPER_ADMIN',
            'LOADING_IMPOERSONATION_SESSION',
            'LOGGING_OUT_OF_CLERK',
            'CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING',
        ].includes(state.value as string)
    ) {
        return renderLoading();
    } else if (state.matches('NO_ACCOUNT')) {
        throw new Error('No account');
    } else if (isSuperAdminRoute && state.matches('IMPOERSONATION_SESSION')) {
        navigate('/');
        return renderLoading();
    } else if (isSuperAdminRoute && !state.matches('SUPER_ADMIN')) {
        navigate('/');
        return renderLoading();
    } else if (!isSuperAdminRoute && state.matches('SUPER_ADMIN')) {
        navigate('/admin');
        return renderLoading();
    } else {
        return renderChildren();
    }
};
